/** One line of a line diff: kept, or only in `after` (added), or only in `before` (removed). */
export interface DiffLine {
  kind: 'same' | 'added' | 'removed';
  text: string;
}

/**
 * ponytail: past this many LCS cells the middle becomes one remove-then-add block
 * instead of a line-level diff. 4M cells is ~16 MB of Uint32 — a 2,000 × 2,000-line
 * edit. A 20k-char soul is a few hundred lines, so only a pathological
 * one-character-per-line input reaches it; Myers' O(ND) is the upgrade if one does.
 */
const MAX_CELLS = 4_000_000;

const toLines = (s: string): string[] => (s === '' ? [] : s.split('\n'));
const tag =
  (kind: DiffLine['kind']) =>
  (text: string): DiffLine => ({ kind, text });

/**
 * Line diff of `before` → `after` by longest common subsequence.
 *
 * ponytail: plain O(n·m) table, no diff library (none is installed and adding one
 * is not allowed). The shared head and tail are trimmed first, so a typical
 * edit to one paragraph only tables the lines that differ.
 */
export function diffLines(before: string, after: string): DiffLine[] {
  const a = toLines(before);
  const b = toLines(after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  return [
    ...a.slice(0, start).map(tag('same')),
    ...lcs(a.slice(start, endA), b.slice(start, endB)),
    ...a.slice(endA).map(tag('same')),
  ];
}

function lcs(a: string[], b: string[]): DiffLine[] {
  const n = a.length;
  const m = b.length;
  if (n * m > MAX_CELLS) return [...a.map(tag('removed')), ...b.map(tag('added'))];
  // at(i, j) = LCS length of a[i:] and b[j:]. Every index below is in range by the
  // loop bounds, so the reads are asserted rather than defaulted.
  const w = m + 1;
  const dp = new Uint32Array((n + 1) * w);
  const at = (i: number, j: number) => dp[i * w + j]!;
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * w + j] = a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i]! });
      i++;
      j++;
    } else if (at(i + 1, j) >= at(i, j + 1)) {
      out.push({ kind: 'removed', text: a[i++]! });
    } else {
      out.push({ kind: 'added', text: b[j++]! });
    }
  }
  out.push(...a.slice(i).map(tag('removed')), ...b.slice(j).map(tag('added')));
  return out;
}
